import { MDXProvider } from "@mdx-js/react";
import { Button, type ButtonVariant } from "@themes/pollinator/ui/Button";
import { useMdxArticleLink } from "./MdxArticleLinkContext";

// MDX often wraps JSX children in a <p> (and 4-space-indented children in a
// <pre>/<code>). Inside a button those block-level wrappers add extra vertical
// space and line breaks, so flatten them to inline content here.
const inlineMdxComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  code: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

export type Props = {
  /** Article slug. Either `slug` or `id` must be provided. If both are passed, `slug` wins. */
  slug?: string;
  /** Article id (currently equal to slug in this codebase — see useFetchArticle.ts). */
  id?: string;
  /** Themed Button variant. Defaults to "primary". */
  variant?: ButtonVariant;
  iconComponent?: React.ComponentType<{ className?: string }>;
  className?: string;
  children?: React.ReactNode;
};

let warnedAboutMissingProvider = false;

export function SupportArticleButton({
  slug,
  id,
  variant = "primary",
  iconComponent,
  className,
  children,
}: Props) {
  const link = useMdxArticleLink();
  const target = slug ?? id;

  const handleClick = () => {
    if (!target) return;
    if (!link) {
      if (!warnedAboutMissingProvider) {
        warnedAboutMissingProvider = true;
        console.warn(
          "<SupportArticleButton> rendered without an <MdxArticleLinkProvider>; click ignored."
        );
      }
      return;
    }
    link.onArticleSelect(target);
  };

  return (
    <Button
      variant={variant}
      iconComponent={iconComponent}
      className={className}
      disabled={!target}
      onClick={handleClick}
      data-testid="support-article-button"
    >
      <MDXProvider components={inlineMdxComponents}>{children}</MDXProvider>
    </Button>
  );
}

export default SupportArticleButton;
